import axios from 'axios'
import { getClientConfig } from '../services/clientConfigService'

// Used only by the legacy client-side Graph fallback (USER_PHOTO_SERVICE off).
const graph = require('@microsoft/microsoft-graph-client')

// Graph photo dimensions (as historically passed by callers) → the server endpoint's `size` token.
const DIMENSIONS_TO_SIZE = { '96x96': '96', '120x120': '120', '240x240': '240', '648x648': 'large', large: 'large' }

let profilePicturesInMemoryCache = {}

/**
 * In-flight requests, keyed like the value cache. The value cache is written only once a fetch resolves
 * and `getProfilePic` awaits `getClientConfig()` before fetching, so the check-then-write window spans a
 * whole config round-trip: without this map every caller arriving inside it — the users grid's per-row
 * `Promise.all` plus each Manager-column cell — misses and opens its own request for the same user.
 * Storing the in-flight promise collapses them onto one, and takes blob-URL creation from N to 1 per key.
 *
 * Evicted on settle, NOT via `.catch()`: both fetch paths swallow their errors and resolve `''` and
 * `getClientConfig` never rejects, so a rejection never occurs — a `.catch()` eviction would never fire,
 * leaking the entry and pinning a transient `''` for the session, which would regress the
 * negative-cache-404-only contract below.
 *
 * @type {Object<string, Promise<string>>} keyed `${userId}:${size}`, mirroring profilePicturesInMemoryCache
 */
const inFlightProfilePicRequests = {}

/**
 * Primary path: resolve a user's avatar via the App backend photo endpoint (GET /api/user/photo/:userId)
 * over the global session axios (Bearer + x-region already attached). 404 → no photo (initials),
 * negative-cached; transient errors are NOT cached.
 */
async function fetchFromEndpoint(userId, size, cacheKey) {
  try {
    // Encode the path segment so a B2B guest UPN (contains '#') or any special char isn't truncated /
    // mangled in the URL; Express decodes req.params.userId server-side. No-op for a GUID oid.
    const res = await axios.get(`/api/user/photo/${encodeURIComponent(userId)}`, {
      params: { size },
      responseType: 'blob'
    })
    const blobUrl = window.URL.createObjectURL(res.data)
    profilePicturesInMemoryCache[cacheKey] = blobUrl
    return blobUrl
  } catch (error) {
    if (error?.response?.status === 404) {
      profilePicturesInMemoryCache[cacheKey] = 'USER_PHOTO_NOT_EXISTS'
    }
    return ''
  }
}

/**
 * Fallback path (USER_PHOTO_SERVICE disabled via /api/client-config): the legacy direct Microsoft Graph
 * avatar fetch with the viewer's delegated token. Kept so photo traffic can be shed off the backend
 * without a redeploy. Negative-caches on any error (mirrors the prior Graph behavior).
 */
async function fetchFromGraph(accessToken, userId, size, dimensions, cacheKey) {
  // No token yet (e.g. before the Graph token loads) — don't negative-cache, so it retries once a token exists.
  if (!accessToken) return ''
  try {
    const client = graph.Client.init({
      authProvider: done => {
        done(null, accessToken)
      }
    })
    // Encode the id so a B2B guest UPN's '#EXT#' isn't truncated at the URL fragment (Graph decodes it back).
    const id = encodeURIComponent(userId)
    const url = size === 'large' ? `users/${id}/photo/$value` : `users/${id}/photos/${dimensions || '96x96'}/$value`
    const res = await client.api(url).responseType('blob').get()
    const blobUrl = window.URL.createObjectURL(res)
    profilePicturesInMemoryCache[cacheKey] = blobUrl
    return blobUrl
  } catch (error) {
    profilePicturesInMemoryCache[cacheKey] = 'USER_PHOTO_NOT_EXISTS'
    return ''
  }
}

/**
 * Resolve a user's avatar ('' when there is no photo → Persona shows initials). The source is chosen by
 * the server-driven USER_PHOTO_SERVICE flag (fetched once per session): on → the App backend photo
 * endpoint (no Graph token); off → the legacy client-side Graph fetch with `accessToken`.
 *
 * Single-flight per `${userId}:${size}`: concurrent callers for one user share one request rather than
 * each opening their own (see inFlightProfilePicRequests).
 *
 * @param {*} accessToken - viewer's Microsoft Graph token; used ONLY by the legacy fallback path.
 * @param {string} userId - the subject user's UPN or Entra object id (the endpoint resolves either).
 * @param {string} [dimensions] - legacy Graph dimension string (e.g. '96x96'); mapped to the size token.
 * @returns {Promise<string>} a blob URL, or '' for no-photo / transient failure.
 */
export async function getProfilePic(accessToken, userId, dimensions) {
  if (!userId) return ''

  const size = DIMENSIONS_TO_SIZE[dimensions] ?? '96'
  const cacheKey = `${userId}:${size}`

  const cached = profilePicturesInMemoryCache[cacheKey]
  if (cached === 'USER_PHOTO_NOT_EXISTS') return ''
  if (cached) return cached

  const inFlight = inFlightProfilePicRequests[cacheKey]
  if (inFlight) return inFlight

  // Built and registered synchronously — before the getClientConfig await — so callers arriving during
  // the config round-trip join this request rather than opening a second one.
  const request = getClientConfig()
    .then(({ userPhotoServiceEnabled }) =>
      userPhotoServiceEnabled
        ? fetchFromEndpoint(userId, size, cacheKey)
        : fetchFromGraph(accessToken, userId, size, dimensions, cacheKey)
    )
    .finally(() => {
      delete inFlightProfilePicRequests[cacheKey]
    })

  inFlightProfilePicRequests[cacheKey] = request
  return request
}
